/**
 * PoolerSyncTab — the bridge between our pallet ledger and the
 * pooler portals (CHEP Portfolio+, Loscam Online).
 *
 * Sections (top → bottom):
 *   1. Provider settings — auto-sync toggle + per-pooler 'mock|real' switch.
 *      'real' falls back to mock when env credentials are missing.
 *   2. Reconciliation grid — system_qty vs pooler_qty per (merchant, type).
 *      "Refresh from pooler" button per row pulls a live balance and stamps
 *      the variance. Clean rows are green, drifted rows are amber.
 *   3. Sync log — recent push attempts (last 50). Each row shows pooler,
 *      docket id, status, error.
 *   4. Run-now action — manual trigger of the daily batch flush.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowsClockwise, CheckCircle, Warning, XCircle, Pulse,
  Cloud, Lightning, ListChecks, EnvelopeSimple, Bell,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import {
  getPoolerSettings,
  updatePoolerSettings,
  refreshPoolerBalance,
  getPoolerReconciliation,
  getPoolerSyncLog,
  getMovementSyncStatus,
  syncMovementNow,
  runPoolerSyncNow,
  previewVarianceDigest,
  sendVarianceDigestNow,
  getVarianceDigestHistory,
} from '../../lib/api';

function StatusBadge({ status }) {
  const map = {
    sent:      { cls: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle, label: 'Sent' },
    confirmed: { cls: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle, label: 'Confirmed' },
    mocked:    { cls: 'bg-sky-100 text-sky-700 border-sky-300', icon: Cloud, label: 'Mock' },
    failed:    { cls: 'bg-rose-100 text-rose-700 border-rose-300', icon: XCircle, label: 'Failed' },
    skipped:   { cls: 'bg-zinc-100 text-zinc-600 border-zinc-300', icon: Warning, label: 'Skipped' },
    pending:   { cls: 'bg-amber-100 text-amber-700 border-amber-300', icon: Pulse, label: 'Pending' },
  };
  const m = map[status] || { cls: 'bg-zinc-100 text-zinc-500 border-zinc-300', icon: Warning, label: status || '—' };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[11px] font-mono uppercase ${m.cls}`}>
      <Icon size={11} weight="bold" /> {m.label}
    </span>
  );
}

export default function PoolerSyncTab() {
  const [settings, setSettings] = useState(null);
  const [reconRows, setReconRows] = useState([]);
  const [logRows, setLogRows] = useState([]);
  const [movRows, setMovRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const [digestHistory, setDigestHistory] = useState([]);

  const refresh = async () => {
    try {
      const [s, r, l, m, h] = await Promise.all([
        getPoolerSettings(),
        getPoolerReconciliation(),
        getPoolerSyncLog({ limit: 50 }),
        getMovementSyncStatus({ limit: 50 }),
        getVarianceDigestHistory({ limit: 5 }),
      ]);
      setSettings(s.data);
      setReconRows(r.data?.rows || []);
      setLogRows(l.data?.rows || []);
      setMovRows(m.data?.rows || []);
      setDigestHistory(h.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load pooler data');
    }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveSettings = async (next) => {
    setBusy(true);
    try {
      await updatePoolerSettings(next);
      toast.success('Pooler settings saved');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const refreshOneBalance = async (row) => {
    if (!row.pooler_account_id || !row.equipment_code) {
      toast.warning('Set a pooler account number on the merchant first');
      return;
    }
    setBusy(true);
    try {
      await refreshPoolerBalance({
        pooler: row.pooler,
        pooler_account_id: row.pooler_account_id,
        equipment_code: row.equipment_code,
      });
      toast.success(`Pulled ${row.pooler.toUpperCase()} balance for ${row.merchant_name}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refresh failed');
    } finally {
      setBusy(false);
    }
  };

  const runBatch = async () => {
    setBusy(true);
    try {
      const r = await runPoolerSyncNow();
      const d = r.data || {};
      toast.success(`Batch flush: ${d.sent} sent · ${d.failed} failed · ${d.skipped} skipped`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Run failed');
    } finally {
      setBusy(false);
    }
  };

  const retryOne = async (movementId) => {
    setBusy(true);
    try {
      await syncMovementNow(movementId);
      toast.success('Movement re-synced');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    } finally {
      setBusy(false);
    }
  };

  const previewDigest = async () => {
    setBusy(true);
    try {
      const r = await previewVarianceDigest({ refresh_first: true });
      setDigestPreview(r.data);
      toast.success(`Preview built · ${r.data.breach_count} breach(es)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setBusy(false);
    }
  };

  const sendDigest = async () => {
    if (!window.confirm('Send the variance digest to admins/managers + extra recipients now?')) return;
    setBusy(true);
    try {
      const r = await sendVarianceDigestNow();
      toast.success(`Digest sent · ${r.data.broadcast?.emails_sent || 0} email(s) · ${r.data.breach_count} breach(es)`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setBusy(false);
    }
  };

  const updateDigestField = (field, value) => {
    if (!settings) return;
    saveSettings({
      ...settings,
      variance_digest: { ...(settings.variance_digest || {}), [field]: value },
    });
  };

  if (!settings) {
    return <div className="p-6 text-center text-muted-foreground">Loading…</div>;
  }

  return (
    <div data-testid="pallet-tab-pooler-sync" className="space-y-8">
      {/* ========== Settings ========== */}
      <section className="bg-white border border-zinc-200 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold tracking-tight">Pooler integration</h2>
            <p className="text-sm text-muted-foreground">
              Push hire / dehire events directly to CHEP Portfolio+ and Loscam Online.
              Real-mode requires your enterprise API credentials in the pod environment;
              missing credentials safely fall back to mock.
            </p>
          </div>
          <Button onClick={runBatch} disabled={busy} className="bg-sky-600 hover:bg-sky-700" data-testid="pooler-run-batch">
            <Lightning size={16} weight="fill" className="mr-1" /> Run sync now
          </Button>
        </div>

        <div className="flex items-center justify-between p-3 bg-zinc-50 border border-zinc-200">
          <div>
            <div className="font-bold text-sm">Auto-sync on every movement</div>
            <div className="text-xs text-muted-foreground">
              When ON, every CHEP/Loscam pallet IN/OUT we record fires a hire/dehire to the pooler in real time.
            </div>
          </div>
          <Switch
            checked={!!settings.auto_sync_enabled}
            onCheckedChange={(v) => saveSettings({ ...settings, auto_sync_enabled: v })}
            disabled={busy}
            data-testid="pooler-auto-sync-toggle"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          {(settings.supported_poolers || ['chep', 'loscam']).map((p) => {
            const cur = settings[p]?.provider || 'mock';
            return (
              <div key={p} className="border border-zinc-200 p-3" data-testid={`pooler-provider-${p}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-bold uppercase tracking-wider text-sm">{p}</div>
                  <span className={`px-2 py-0.5 text-[11px] font-mono uppercase border ${
                    cur === 'real' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : 'bg-sky-100 text-sky-700 border-sky-300'
                  }`}>
                    {cur === 'real' ? 'Real API' : 'Mock'}
                  </span>
                </div>
                <div className="flex gap-2">
                  {['mock', 'real'].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      data-testid={`pooler-${p}-set-${mode}`}
                      onClick={() => saveSettings({
                        ...settings,
                        [p]: { provider: mode },
                      })}
                      disabled={busy || cur === mode}
                      className={`flex-1 px-3 py-1.5 text-xs font-bold uppercase border transition ${
                        cur === mode
                          ? 'bg-zinc-900 text-white border-zinc-900'
                          : 'bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900'
                      }`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
                {p === 'chep' && cur === 'real' && (
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Set <code>CHEP_CLIENT_ID</code> + <code>CHEP_CLIENT_SECRET</code> in the backend env.
                    Falls back to mock if missing.
                  </p>
                )}
                {p === 'loscam' && cur === 'real' && (
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Set <code>LOSCAM_API_KEY</code> + <code>LOSCAM_ACCOUNT_NUMBER</code> in the backend env.
                    Falls back to mock if missing.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ========== Variance digest ========== */}
      <section className="bg-white border border-zinc-200 p-5 space-y-4" data-testid="pooler-variance-digest">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-lg font-bold tracking-tight flex items-center gap-2">
              <Bell size={18} weight="bold" /> Variance digest
            </h2>
            <p className="text-sm text-muted-foreground">
              Daily + weekly email/Slack digest comparing system vs pooler balances. Triggered when any
              (merchant × type) row drifts past either threshold (whichever fires first). Recipients =
              tenant admins/managers + the extras below.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={previewDigest}
              disabled={busy}
              variant="outline"
              data-testid="pooler-digest-preview"
            >
              <EnvelopeSimple size={16} className="mr-1" /> Preview now
            </Button>
            <Button
              onClick={sendDigest}
              disabled={busy}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="pooler-digest-send-now"
            >
              <Lightning size={16} weight="fill" className="mr-1" /> Send now
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div className="flex items-center justify-between p-3 bg-zinc-50 border border-zinc-200">
            <div>
              <div className="font-bold text-sm">Daily digest</div>
              <div className="text-xs text-muted-foreground">~03:00 UTC, every day</div>
            </div>
            <Switch
              checked={!!(settings.variance_digest?.daily_enabled ?? true)}
              onCheckedChange={(v) => updateDigestField('daily_enabled', v)}
              disabled={busy}
              data-testid="pooler-digest-daily-toggle"
            />
          </div>
          <div className="flex items-center justify-between p-3 bg-zinc-50 border border-zinc-200">
            <div>
              <div className="font-bold text-sm">Weekly summary</div>
              <div className="text-xs text-muted-foreground">Mondays at 09:00 UTC</div>
            </div>
            <Switch
              checked={!!(settings.variance_digest?.weekly_enabled ?? true)}
              onCheckedChange={(v) => updateDigestField('weekly_enabled', v)}
              disabled={busy}
              data-testid="pooler-digest-weekly-toggle"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          <label className="block">
            <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Threshold (pallets)</div>
            <Input
              type="number" min="1"
              value={settings.variance_digest?.threshold_abs ?? 10}
              onChange={(e) => updateDigestField('threshold_abs', parseInt(e.target.value || '10', 10))}
              data-testid="pooler-digest-threshold-abs"
              className="font-mono"
            />
          </label>
          <label className="block">
            <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Threshold (% of system_qty)</div>
            <Input
              type="number" min="0" step="0.5"
              value={settings.variance_digest?.threshold_pct ?? 5.0}
              onChange={(e) => updateDigestField('threshold_pct', parseFloat(e.target.value || '5'))}
              data-testid="pooler-digest-threshold-pct"
              className="font-mono"
            />
          </label>
          <label className="block">
            <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Extra recipients (comma-sep)</div>
            <Input
              type="text"
              defaultValue={(settings.variance_digest?.extra_recipients || []).join(', ')}
              onBlur={(e) => updateDigestField(
                'extra_recipients',
                e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
              )}
              placeholder="controller@example.com, ops@example.com"
              data-testid="pooler-digest-extras"
            />
          </label>
        </div>

        {digestPreview && (
          <div className="border border-zinc-300 bg-zinc-50 p-3" data-testid="pooler-digest-preview-output">
            <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-1">
              Preview · {digestPreview.row_count} rows · {digestPreview.breach_count} breach(es) · refreshed {digestPreview.refreshed} balance(s)
            </div>
            <div className="font-mono text-xs whitespace-pre-wrap leading-relaxed max-h-72 overflow-auto">{digestPreview.body}</div>
          </div>
        )}

        {digestHistory.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-1">Recent digests</div>
            <div className="text-xs text-muted-foreground space-y-1">
              {digestHistory.map((h) => (
                <div key={h.id} data-testid={`pooler-digest-history-${h.id}`} className="flex justify-between">
                  <span>{new Date(h.published_at).toLocaleString()} · <span className="uppercase font-bold">{h.period_label}</span></span>
                  <span>{h.row_count} rows · {h.breach_count} breach(es) · refreshed {h.refreshed}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ========== Reconciliation grid ========== */}
      <section className="bg-white border border-zinc-200 p-5 space-y-3" data-testid="pooler-reconciliation-grid">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold tracking-tight flex items-center gap-2">
              <ListChecks size={18} weight="bold" /> System vs pooler reconciliation
            </h2>
            <p className="text-sm text-muted-foreground">
              Per merchant × pallet type. Variance = pooler_qty − system_qty.
              Click "Refresh" to pull a fresh balance from the pooler portal.
            </p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-zinc-200">
              <tr>
                <th className="text-left p-2">Merchant</th>
                <th className="text-left p-2">Type</th>
                <th className="text-left p-2">Pooler</th>
                <th className="text-left p-2">Account</th>
                <th className="text-right p-2">System</th>
                <th className="text-right p-2">Pooler</th>
                <th className="text-right p-2">Δ</th>
                <th className="text-left p-2">Last checked</th>
                <th className="text-right p-2">Actions</th>
              </tr>
            </thead>
            <tbody className="font-mono text-[13px]">
              {reconRows.length === 0 && (
                <tr><td colSpan={9} className="p-6 text-center text-muted-foreground italic">
                  No pooled stock yet. Record an IN/OUT movement on a CHEP or Loscam pallet to populate this grid.
                </td></tr>
              )}
              {reconRows.map((r, i) => {
                const v = r.variance;
                const cls = v === null ? 'text-zinc-400'
                          : v === 0    ? 'text-emerald-700'
                          : 'text-amber-700';
                return (
                  <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`pooler-recon-row-${r.pooler}-${r.merchant_id || 'none'}`}>
                    <td className="p-2 font-sans">{r.merchant_name || '—'}</td>
                    <td className="p-2">{r.type_code}</td>
                    <td className="p-2 uppercase">{r.pooler}</td>
                    <td className="p-2">{r.pooler_account_id || <span className="text-rose-500">missing</span>}</td>
                    <td className="p-2 text-right">{r.system_qty}</td>
                    <td className="p-2 text-right">{r.pooler_qty ?? <span className="text-zinc-400">—</span>}</td>
                    <td className={`p-2 text-right font-bold ${cls}`}>{v === null ? '—' : (v > 0 ? `+${v}` : v)}</td>
                    <td className="p-2 font-sans text-xs text-muted-foreground">
                      {r.fetched_at ? new Date(r.fetched_at).toLocaleString() : '—'}
                      {r.is_mock && <span className="ml-1 text-sky-600">(mock)</span>}
                    </td>
                    <td className="p-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy || !r.pooler_account_id}
                        onClick={() => refreshOneBalance(r)}
                        data-testid={`pooler-recon-refresh-${i}`}
                      >
                        <ArrowsClockwise size={12} className="mr-1" /> Refresh
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* ========== Sync log ========== */}
      <section className="bg-white border border-zinc-200 p-5 space-y-3" data-testid="pooler-sync-log">
        <h2 className="text-lg font-bold tracking-tight">Sync log</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-zinc-200">
              <tr>
                <th className="text-left p-2">When</th>
                <th className="text-left p-2">Pooler</th>
                <th className="text-left p-2">Docket</th>
                <th className="text-left p-2">Movement</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Error</th>
              </tr>
            </thead>
            <tbody className="font-mono text-[13px]">
              {logRows.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-muted-foreground italic">No sync attempts yet.</td></tr>
              )}
              {logRows.map((r) => (
                <tr key={r.id} className="border-b border-zinc-100">
                  <td className="p-2 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="p-2 uppercase">{r.pooler}</td>
                  <td className="p-2">{r.pooler_movement_id || '—'}</td>
                  <td className="p-2 text-xs text-muted-foreground">{r.movement_id?.slice(-8)}</td>
                  <td className="p-2"><StatusBadge status={r.status} /></td>
                  <td className="p-2 text-rose-600 text-xs truncate max-w-[300px]">{r.error || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ========== Recent movements with sync state + retry ========== */}
      <section className="bg-white border border-zinc-200 p-5 space-y-3" data-testid="pooler-movement-status">
        <h2 className="text-lg font-bold tracking-tight">Recent movements & sync state</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-zinc-200">
              <tr>
                <th className="text-left p-2">When</th>
                <th className="text-left p-2">Type</th>
                <th className="text-left p-2">Kind</th>
                <th className="text-right p-2">Qty</th>
                <th className="text-left p-2">Pooler</th>
                <th className="text-left p-2">Docket</th>
                <th className="text-left p-2">Status</th>
                <th className="text-right p-2">Action</th>
              </tr>
            </thead>
            <tbody className="font-mono text-[13px]">
              {movRows.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-muted-foreground italic">No recent movements.</td></tr>
              )}
              {movRows.map((m) => {
                const isPooled = (m.type_code || '').toUpperCase().includes('CHEP') || (m.type_code || '').toUpperCase().includes('LOSCAM');
                const canRetry = isPooled && (m.pooler_sync_status === 'failed' || m.pooler_sync_status === 'pending' || m.pooler_sync_status === 'skipped');
                return (
                  <tr key={m.id} className="border-b border-zinc-100">
                    <td className="p-2 text-xs text-muted-foreground">{new Date(m.created_at).toLocaleString()}</td>
                    <td className="p-2">{m.type_code}</td>
                    <td className="p-2 uppercase">{m.kind}</td>
                    <td className="p-2 text-right">{m.qty}</td>
                    <td className="p-2 uppercase">{m.pooler_sync_pooler || (isPooled ? '—' : 'n/a')}</td>
                    <td className="p-2">{m.pooler_sync_id || '—'}</td>
                    <td className="p-2">
                      {isPooled
                        ? <StatusBadge status={m.pooler_sync_status || 'pending'} />
                        : <span className="text-zinc-400 text-[11px] uppercase">owned</span>}
                      {m.pooler_sync_skip_reason && (
                        <div className="text-[10px] text-muted-foreground mt-0.5">{m.pooler_sync_skip_reason}</div>
                      )}
                    </td>
                    <td className="p-2 text-right">
                      {canRetry && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={() => retryOne(m.id)}
                          data-testid={`pooler-retry-${m.id}`}
                        >
                          <ArrowsClockwise size={12} className="mr-1" /> Retry
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
